import {
  alpha,
  Box,
  BoxProps,
  colors,
  Fade,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from '@material-ui/core';
import React, { KeyboardEventHandler, MouseEventHandler } from 'react';
import useStyles from './styles';
import Input from '@material-ui/core/Input';
import SendIcon from '@material-ui/icons/Send';
import Avatar from '@material-ui/core/Avatar';
import {
  useConversation,
  useChatgroupDetails,
  useChatData,
} from 'providers/SocketioProvider/chat/selectors';
import { useForm, useWatch } from 'react-hook-form';
import { addMessage, readChat } from 'providers/SocketioProvider/chat/actions';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import MemberAvatar from '../MemberAvatar';
import { IChatgroupDetails } from 'services/api/chat';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Environment } from 'config';
import NewLineText from 'components/atoms/NewLineText';
import UploadImageButton from './UploadImageButton';
import { MessageType } from 'providers/SocketioProvider/chat/config';
import ImageShow from 'components/app_common/ImageShow';

interface IConversationProps {
  chatgroupId: string | null;
  contentHeight?: number;
  containerProps?: Omit<BoxProps, 'children'>;
  open?: boolean;
  onOpen?: MouseEventHandler<HTMLDivElement>;
  onClose?: (e: any) => void;
  // React.MouseEventHandler<HTMLButtonElement>;
}

// IChatgroupDetails
const ConversationContext = React.createContext<IChatgroupDetails | null>(null);

const Conversation = (props: IConversationProps) => {
  const classes = useStyles();
  const details = useChatgroupDetails(props.chatgroupId);

  return (
    <ConversationContext.Provider value={details.data?.data ?? null}>
      <Box
        style={{ height: props.contentHeight }}
        {...props.containerProps}
        className={classes.root}
      >
        <HeaderWrapper {...props} />
        <MessagesContainer {...props} />
        <MessageInput {...props} />
      </Box>
    </ConversationContext.Provider>
  );
};

const MessagesContainer = ({
  chatgroupId,
  open,
  onClose,
}: IConversationProps) => {
  const [pageSize, setPageSize] = React.useState(20);
  const { conversation, socketMessage } = useConversation(chatgroupId, {
    pageSize,
  });
  const messagesEndRef = React.useRef<any>(null);

  React.useLayoutEffect(() => {
    if (socketMessage) {
      messagesEndRef.current?.scrollIntoView();
    }
  }, [conversation, socketMessage]);

  const fetchMoreData = React.useCallback(() => {
    // console.log('Fetch more????????????????????????');
    setPageSize((page) => page + 10);
  }, [setPageSize]);

  return (
    <Fade in={open ?? true} timeout={300}>
      <Box
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column-reverse',
          overflow: 'auto',
          paddingBottom: '1rem',
        }}
        className={!onClose ? 'chat-scrollbar' : undefined}
        id="scrollableDiv"
      >
        <div ref={messagesEndRef} />
        <InfiniteScroll
          dataLength={conversation?.items.length ?? 0}
          next={fetchMoreData}
          style={{
            display: 'flex',
            flexDirection: 'column-reverse',
          }} //To put endMessage and loader to the top.
          hasMore={
            (conversation?.items.length ?? 0) <
            (conversation?.paging.total ?? 0)
          }
          inverse
          loader={
            <div style={{ width: '100%', textAlign: 'center' }}>
              <h5>Loading...</h5>
            </div>
          }
          scrollableTarget="scrollableDiv"
          endMessage={
            <p style={{ textAlign: 'center' }}>
              <b>Yay! You have seen it all</b>
            </p>
          }
        >
          {conversation?.items.map((data, index, arr) => {
            return (
              <Message
                messageOwner={
                  conversation.currentMember.id === data.chatentityid
                }
                onClose={onClose ?? null}
                key={index}
                message={data.message}
                senderName={data.senderName || 'Guest user'}
                messageType={data.type}
                pictureSrc={
                  data.virtualpath
                    ? `${Environment.API_BASE_URL}/${data.virtualpath}`
                    : undefined
                }
              />
            );
          })}
        </InfiniteScroll>
      </Box>
    </Fade>
  );
};

const HeaderWrapper = (props: IConversationProps) => {
  const details = React.useContext(ConversationContext);

  return <>{details && <Header {...props} details={details} />}</>;
};

const Header = ({
  chatgroupId,
  open,
  onOpen,
  onClose,
  details,
}: IConversationProps & { details: IChatgroupDetails }) => {
  const classes = useStyles();
  const _details = useChatData(chatgroupId, details);
  const online = (_details.onlineMembers ?? 0) > 1;

  return (
    <Paper
      square
      variant="outlined"
      style={{
        minHeight: 50,
        width: '100%',
        borderLeftWidth: 0,
        borderRightWidth: 0,
        paddingTop: '0.4rem',
        paddingBottom: '0.4rem',
        borderTopWidth: 0,
        cursor: 'pointer',
      }}
      onClick={onOpen}
    >
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            marginLeft: '1rem',
          }}
        >
          <MemberAvatar
            src={
              details.virtualpath
                ? `${Environment.API_BASE_URL}/${details.virtualpath}`
                : undefined
            }
            online={online}
          />
          <div style={{ marginLeft: '0.5rem' }}>
            <Typography style={{ fontSize: '1.1em' }}>
              {details?.title}
            </Typography>
            <Typography style={{ fontSize: '0.8em' }}>
              {online ? 'Online' : 'Offline'}
            </Typography>
          </div>
        </Box>
        {onClose && (
          <Zoom in={open ?? true} timeout={300}>
            <Box style={{ marginRight: '0.5rem' }}>
              <IconButton onClick={onClose} size="small">
                <CloseOutlinedIcon />
              </IconButton>
            </Box>
          </Zoom>
        )}
      </Box>
    </Paper>
  );
};

interface IFormData {
  message: string;
}

const defaultValues: IFormData = {
  message: '',
};

const MessageInput = React.memo(
  ({ chatgroupId, onClose }: IConversationProps) => {
    const { handleSubmit, register, reset, control, setFocus } = useForm({
      defaultValues,
    });

    const details = React.useContext(ConversationContext);

    const onSubmit = React.useCallback(
      handleSubmit((data: IFormData) => {
        if (chatgroupId && data.message.length > 0) {
          addMessage({
            message: data.message,
            chatGroupGuid: chatgroupId,
            type: MessageType.text,
          });
        }
        reset();
      }),
      [handleSubmit, chatgroupId]
    );

    React.useEffect(() => {
      if (details?.id) {
        setFocus('message');
      }
    }, [details?.id, setFocus]);

    return (
      <>
        {details?.id && (
          <Box>
            <form onSubmit={onSubmit}>
              <Paper
                square
                variant="outlined"
                style={{
                  width: '100%',
                  paddingLeft: '0.5rem',
                  paddingRight: '0.5rem',
                  paddingTop: '0.5rem',
                  paddingBottom: '0.5rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderWidth: 0,
                  borderTopWidth: '2px',
                  // background: alpha(colors.grey['200'], 0.5),
                }}
              >
                {chatgroupId && <UploadImageButton chatgroupId={chatgroupId} />}
                <Input
                  placeholder="Type your message"
                  fullWidth
                  disableUnderline
                  autoComplete="off"
                  multiline
                  maxRows={5}
                  // onKeyDown={onSubmit}
                  onFocus={() => {
                    // read conversation
                    chatgroupId && readChat({ chatGroupGuid: chatgroupId });
                  }}
                  {...register('message')}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      onClose && onClose(e);
                    } else if (e.key === 'Enter' && e.shiftKey == false) {
                      e.preventDefault();
                      onSubmit();
                    }
                    console.log(e.key);
                  }}
                  inputProps={{
                    'aria-label': 'description',
                    style: {
                      borderWidth: 0,
                    },
                  }}
                />
                <SendButton onSubmit={onSubmit} control={control} />
              </Paper>
            </form>
          </Box>
        )}
      </>
    );
  }
);

interface ISendButtonProps {
  control: any;
  onSubmit: () => void;
}

const SendButton = ({ onSubmit, control }: ISendButtonProps) => {
  const message = useWatch({
    control,
    name: 'message',
  });

  return (
    <IconButton
      disabled={message.length === 0}
      onClick={() => {
        onSubmit();
      }}
      color="primary"
      size="small"
    >
      <SendIcon />
    </IconButton>
  );
};

interface IMessageProps {
  messageOwner: boolean;
  message: string;
  pictureSrc?: string;
  senderName: string;
  onClose: MouseEventHandler<HTMLButtonElement> | null;
  messageType: MessageType | null;
}

const Message = (props: IMessageProps) => {
  const { messageOwner, pictureSrc, senderName, onClose } = props;
  const classes = useStyles();
  return (
    <Box
      style={{
        marginTop: '1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: messageOwner ? 'flex-end' : 'flex-start',
        marginLeft: '1rem',
        marginRight: '1rem',
      }}
    >
      {messageOwner ? (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            alignSelf: 'flex-end',
            maxWidth: onClose ? '100%' : '50%',
            // overflow: 'hidden',
          }}
        >
          <MessageBox {...props} />
        </Box>
      ) : (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            flex: 1,
            maxWidth: onClose ? '100%' : '50%',
            // overflow: 'hidden',
          }}
        >
          <Avatar src={pictureSrc} className={classes.orange}>
            N
          </Avatar>
          <div>
            <Typography style={{ fontSize: 12, marginBottom: '0.1rem' }}>
              {senderName}
            </Typography>
            <MessageBox {...props} />
          </div>
        </Box>
      )}
    </Box>
  );
};

const MessageBox = ({
  messageOwner,
  message,
  senderName,
  messageType,
}: IMessageProps) => {
  const theme = useTheme();

  const TextMessage = () => (
    <Box
      style={{
        padding: '0.25rem 1rem 0.25rem 1rem',
        borderRadius: '0.5rem',
        background: messageOwner
          ? theme.palette.secondary.main
          : colors.common.white,
        display: 'block',
      }}
    >
      <Typography
        style={{
          color: messageOwner
            ? theme.palette.secondary.contrastText
            : colors.common.black,
          overflowWrap: 'anywhere',
          wordWrap: 'break-word',
          hyphens: 'auto',
          wordBreak: 'break-all',
        }}
        component="p"
      >
        <NewLineText text={message} />
      </Typography>
    </Box>
  );

  const ImageMessage = () => {
    const url = `${Environment.API_BASE_URL}/${message}`;
    return (
      <Box
        onClick={() => window.open(url, '_black')}
        style={{ cursor: 'pointer' }}
      >
        <ImageShow picture={url} />
      </Box>
    );
  };

  // style={{ position: 'relative' }}
  return (
    <>
      {(messageType === MessageType.text || !messageType) && <TextMessage />}
      {messageType === MessageType.image && <ImageMessage />}
    </>
  );
};

export default Conversation;
